import React, { useState } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import UserStatusIndicator from '../../components/ui/UserStatusIndicator';
import CallStatusWidget from '../../components/ui/CallStatusWidget';
import MetricsOverview from './components/MetricsOverview';
import CallVolumeChart from './components/CallVolumeChart';
import PerformanceMetrics from './components/PerformanceMetrics';
import RealtimeMonitor from './components/RealtimeMonitor';
import ReportGenerator from './components/ReportGenerator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CallAnalytics = () => {
  const [activeView, setActiveView] = useState('overview');
  const [dateRange, setDateRange] = useState('7d');

  const viewOptions = [
    { id: 'overview', label: 'Genel Bakış', icon: 'BarChart3' },
    { id: 'realtime', label: 'Anlık İzleme', icon: 'Activity' },
    { id: 'reports', label: 'Rapor Oluştur', icon: 'FileText' }
  ];

  const dateRangeOptions = [
    { value: '24h', label: '24 Saat' },
    { value: '7d', label: '7 Gün' },
    { value: '30d', label: '30 Gün' },
    { value: '90d', label: '90 Gün' }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <NavigationBar />
      <UserStatusIndicator />
      <CallStatusWidget />
      <div className="content-offset px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Çağrı Analitikleri</h1>
                <p className="text-lg text-slate-600 mt-2">
                  Kapsamlı performans analizi ve raporlama sistemi
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Date Range Selector */}
                <div className="flex items-center space-x-1 bg-white rounded-lg border border-slate-200 p-1">
                  {dateRangeOptions?.map((option) => (
                    <button
                      key={option?.value}
                      onClick={() => setDateRange(option?.value)}
                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        dateRange === option?.value
                          ? 'bg-primary text-white shadow-sm'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      {option?.label}
                    </button>
                  ))}
                </div>

                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Yenile
                </Button>
              </div>
            </div>

            {/* View Navigation */}
            <div className="flex items-center space-x-1 bg-white rounded-lg border border-slate-200 p-1 w-fit">
              {viewOptions?.map((view) => (
                <button
                  key={view?.id}
                  onClick={() => setActiveView(view?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeView === view?.id
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <Icon name={view?.icon} size={16} />
                  <span>{view?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="space-y-8">
            {activeView === 'overview' && (
              <>
                {/* Metrics Overview */}
                <MetricsOverview />

                {/* Charts Section */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  <CallVolumeChart />
                  <PerformanceMetrics />
                </div>

                {/* Additional Analytics */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Service Level Trends */}
                  <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-subtle">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-900">Hizmet Seviyesi Trendi</h3>
                      <Icon name="TrendingUp" size={20} color="var(--color-success)" />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Bu Hafta</span>
                        <span className="text-lg font-semibold text-slate-900">94.2%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '94.2%' }}></div>
                      </div>
                      <div className="text-xs text-slate-500">
                        Hedef: 90% | Geçen hafta: 91.8%
                      </div>
                    </div>
                  </div>

                  {/* Top Performing Agents */}
                  <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-subtle">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-900">En İyi Performans</h3>
                      <Icon name="Award" size={20} color="var(--color-warning)" />
                    </div>
                    <div className="space-y-3">
                      {[
                        { name: 'Ahmet Y.', score: 98, calls: 156 },
                        { name: 'Elif K.', score: 96, calls: 142 },
                        { name: 'Mehmet S.', score: 94, calls: 138 }
                      ]?.map((agent, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                              index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : 'bg-amber-600'
                            }`}>
                              {index + 1}
                            </div>
                            <span className="text-sm font-medium text-slate-900">{agent?.name}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-slate-900">{agent?.score}%</div>
                            <div className="text-xs text-slate-500">{agent?.calls} çağrı</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent Alerts */}
                  <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-subtle">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-900">Son Uyarılar</h3>
                      <Icon name="Bell" size={20} color="var(--color-error)" />
                    </div>
                    <div className="space-y-3">
                      {[
                        { type: 'warning', message: 'Yüksek terk oranı tespit edildi', time: '5 dk önce' },
                        { type: 'info', message: 'Yeni performans raporu hazır', time: '15 dk önce' },
                        { type: 'success', message: 'Hedef hizmet seviyesine ulaşıldı', time: '1 saat önce' }
                      ]?.map((alert, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            alert?.type === 'warning' ? 'bg-yellow-500' :
                            alert?.type === 'success' ? 'bg-green-500' : 'bg-blue-500'
                          }`}></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900">{alert?.message}</p>
                            <p className="text-xs text-slate-500">{alert?.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeView === 'realtime' && <RealtimeMonitor />}
            
            {activeView === 'reports' && <ReportGenerator />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallAnalytics;